// src/lib/profile.ts
import { createClient } from '@/lib/supabase/browser';
import type { SupabaseClient } from '@supabase/supabase-js';

type AnyClient = SupabaseClient<any, any, any>;

/**
 * Safe read for one profile row by auth user_id.
 * Uses maybeSingle() (no limit/order needed for a unique constraint).
 */
export async function getProfileByUserId(client: AnyClient, userId: string) {
  const { data, error } = await client
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  return { data, error };
}

/**
 * Convenience browser getter when you don't have a client in scope.
 */
export async function getMyProfileSafe() {
  const supabase = createClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('No auth user') };
  return getProfileByUserId(supabase, user.id);
}

// Tolerant profile fetch: never throws on 406, returns null if missing.
export async function getProfileOrNull(userId: string) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .maybeSingle();

  if (error) {
    console.warn("[getProfileOrNull] error:", error);
    return null;
  }
  return data ?? null;
}

/**
 * "Onboarding done" check used by /home, the OnboardingBanner, and any
 * other surface that decides whether to send a user back through
 * /onboarding. The canonical signal is profiles.onboarding_completed_at
 * (set by the OnboardingClient finish handler). Three additional signals
 * are honored for backwards-compatibility:
 *   - onboarding_completed (legacy boolean)
 *   - onboarding_step >= 5 (pre-migration accounts that finished before
 *     the timestamp column existed)
 *   - onboarding_step === -1 (user explicitly chose Skip)
 *
 * If any of those say "done", treat the profile as complete. Do NOT add
 * field-level requirements (display_name / username / title) — the
 * canonical flow doesn't necessarily capture all of those, and adding
 * them here would re-introduce the redirect loop where /home sees the
 * profile as 'incomplete' and bounces to /onboarding even though the
 * user just finished.
 */
export function isProfileComplete(p: any | null): boolean {
  if (!p) return false;
  if (p.onboarding_completed_at) return true;
  if (p.onboarding_completed === true) return true;
  if (typeof p.onboarding_step === 'number') {
    if (p.onboarding_step === -1) return true; // explicitly skipped
    if (p.onboarding_step >= 5) return true; // finished pre-timestamp era
  }
  return false;
}
